/**
 * Central registry of all SDK event bus keys.
 *
 * Every event name used across the app must be defined here. This prevents
 * typo-silent mismatches and ensures that renaming an event only requires a
 * change in this file.
 *
 * Usage:
 *   import { eventKeys } from '@/lib/eventKeys';
 *
 *   useGetEventBus(eventKeys.mails.updated, handler)
 *   offMailUpdated(eventKeys.mails.updated, handler)
 */

export const eventKeys = {
    // ─── Agenda ───────────────────────────────────────────────────────────────
    agenda: {
        itemUpdated: "agenda-item-updated",
        newItem: "agenda-item-new-item",
    },

    // ─── Analysis ─────────────────────────────────────────────────────────────
    analysis: {
        agentSuccess: "analysis-agent-success",
        agentError: "analysis-agent-error",
    },

    // ─── Assistant ────────────────────────────────────────────────────────────
    assistant: {
        reasoningProgress: "assistant-reasoning-progress",
        contextSize: "assistant-context-size",
        usage: "assistant-usage",
        facts: "assistant-facts",
        conversationSaved: "assistant-conversation-saved",
    },

    // ─── Banner ───────────────────────────────────────────────────────────────
    banner: {
        updated: "banner-updated",
    },

    // ─── Custom Views ─────────────────────────────────────────────────────────
    customView: {
        listUpdated: "custom-view-list-updated",
    },

    // ─── Dashboard ────────────────────────────────────────────────────────────
    dashboard: {
        favoritesUpdated: "dashboard-favorite-views-updated",
    },

    // ─── Feature Flags ────────────────────────────────────────────────────────
    featureFlag: {
        updated: "feature-flags-updated",
    },

    // ─── Mails ────────────────────────────────────────────────────────────────
    mails: {
        updated: "mail-updated",
        deleted: "mail-deleted",
        newInbox: "mails-new-mails",
        threadUpdated: "thread-updated",
    },

    // ─── Navigation ───────────────────────────────────────────────────────────
    navigation: {
        updated: "navigation-updated",
    },

    // ─── Network ──────────────────────────────────────────────────────────────
    network: {
        offlineThreshold: "network-offline-threshold-reached",
        online: "network-online",
    },

    // ─── Startup ──────────────────────────────────────────────────────────────
    startup: {
        failed: "startup-failed",
    },

    // ─── Todos ────────────────────────────────────────────────────────────────
    todos: {
        updated: "user-todos-updated",
    },

    // ─── Token ────────────────────────────────────────────────────────────────
    token: {
        iaUpdated: "ia-token-updated",
    },

    // ─── User ─────────────────────────────────────────────────────────────────
    user: {
        authenticated: "user-authenticated",
    },

    // ─── Welcome Messages ─────────────────────────────────────────────────────
    welcomeMessages: {
        updated: "dashboard-welcome-messages-updated",
    },
} as const;

export type EventKey =
    (typeof eventKeys)[keyof typeof eventKeys][keyof (typeof eventKeys)[keyof typeof eventKeys]];
